import {
  BlobServiceClient,
  ContainerClient
} from '@azure/storage-blob';
import { TokenCredential } from '@azure/identity';
import { BlobItem } from '../models/BlobItem';
import { storageConfig } from '../config/authConfig';

export class StorageService {
  private blobServiceClient: BlobServiceClient;
  private credential: TokenCredential;

  constructor(credential: TokenCredential) {
    const baseUrl = `https://${storageConfig.accountName}.blob.core.windows.net`;
    this.blobServiceClient = new BlobServiceClient(baseUrl, credential);
    this.credential = credential;
  }

  async getContainers(): Promise<string[]> {
    const containers: string[] = [];
    
    try {
      for await (const container of this.blobServiceClient.listContainers()) {
        containers.push(container.name);
      }
    } catch (error) {
      console.error('Error fetching containers:', error);
      throw error;
    }
    
    return containers;
  }

  async getBlobsInContainer(containerName: string, folderPath: string = ''): Promise<BlobItem[]> {
    const containerClient: ContainerClient = this.blobServiceClient.getContainerClient(containerName);
    const blobs: BlobItem[] = [];
    
    try {
      // Use hierarchical listing to get folders and files
      const prefix = folderPath ? (folderPath.endsWith('/') ? folderPath : folderPath + '/') : '';
      
      for await (const item of containerClient.listBlobsByHierarchy('/', { prefix })) {
        if (item.kind === 'prefix') {
          // This is a folder/directory
          const folderName = item.name.replace(prefix, '').replace(/\/$/, '');
          blobs.push({
            name: folderName,
            contentLength: 0,
            contentType: 'application/x-directory',
            lastModified: new Date(),
            isFolder: true,
            path: item.name
          });
        } else {
          // This is a file
          const blobClient = containerClient.getBlobClient(item.name);
          const fileName = item.name.replace(prefix, '');
          
          blobs.push({
            name: fileName,
            contentLength: item.properties.contentLength || 0,
            contentType: item.properties.contentType || 'unknown',
            lastModified: item.properties.lastModified || new Date(),
            url: blobClient.url,
            isFolder: false,
            path: item.name
          });
        }
      }
    } catch (error) {
      console.error(`Error fetching blobs from container ${containerName}:`, error);
      throw error;
    }
    
    return blobs;
  }

  async uploadBlob(containerName: string, file: File, folderPath: string = ''): Promise<string> {
    const containerClient: ContainerClient = this.blobServiceClient.getContainerClient(containerName);
    
    // Construct the full blob path
    const blobPath = folderPath 
      ? (folderPath.endsWith('/') ? folderPath + file.name : folderPath + '/' + file.name)
      : file.name;
    
    const blobClient = containerClient.getBlobClient(blobPath);
    const blockBlobClient = blobClient.getBlockBlobClient();
    
    try {
      await blockBlobClient.uploadData(await file.arrayBuffer(), {
        blobHTTPHeaders: {
          blobContentType: file.type
        }
      });
      return blobClient.url;
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      throw error;
    }
  }

  async deleteBlob(containerName: string, blobName: string, folderPath: string = ''): Promise<void> {
    const containerClient: ContainerClient = this.blobServiceClient.getContainerClient(containerName);
    
    // Construct the full blob path
    const blobPath = folderPath 
      ? (folderPath.endsWith('/') ? folderPath + blobName : folderPath + '/' + blobName)
      : blobName;
    
    const blobClient = containerClient.getBlobClient(blobPath);

    try {
      await blobClient.delete();
    } catch (error) {
      console.error(`Error deleting blob ${blobName}:`, error);
      throw error;
    }
  }

  // Delete a folder and everything under it. The account uses a hierarchical
  // namespace (ADLS Gen2), so a folder is a real directory — delete it
  // recursively via the Data Lake (dfs) REST endpoint with an AAD token.
  async deleteFolder(containerName: string, folderPath: string): Promise<void> {
    const dirPath = folderPath.replace(/\/$/, '');
    const encodedPath = dirPath.split('/').map(encodeURIComponent).join('/');
    try {
      const tokenResponse = await this.credential.getToken('https://storage.azure.com/.default');
      if (!tokenResponse) {
        throw new Error('Could not acquire a storage token.');
      }
      const url = `https://${storageConfig.accountName}.dfs.core.windows.net/${containerName}/${encodedPath}?recursive=true`;
      const resp = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenResponse.token}`,
          'x-ms-version': '2021-06-08'
        }
      });
      if (!resp.ok && resp.status !== 404) {
        throw new Error(`Failed to delete folder (HTTP ${resp.status})`);
      }
    } catch (error) {
      console.error(`Error deleting folder ${folderPath}:`, error);
      throw error;
    }
  }

  async createFolder(containerName: string, folderPath: string): Promise<void> {
    const containerClient: ContainerClient = this.blobServiceClient.getContainerClient(containerName);
    
    // Ensure folder path ends with /
    const normalizedPath = folderPath.endsWith('/') ? folderPath : folderPath + '/';
    
    // With hierarchical namespace, create a zero-length blob with the folder path
    // This creates the folder structure
    const blobClient = containerClient.getBlobClient(normalizedPath);
    const blockBlobClient = blobClient.getBlockBlobClient();
    
    try {
      // Upload empty content to create the folder marker
      await blockBlobClient.upload('', 0, {
        metadata: {
          hdi_isfolder: 'true'
        },
        blobHTTPHeaders: {
          blobContentType: 'application/x-directory'
        }
      });
    } catch (error) {
      console.error(`Error creating folder ${folderPath}:`, error);
      throw error;
    }
  }

  async createContainer(containerName: string): Promise<void> {
    try {
      await this.blobServiceClient.createContainer(containerName);
    } catch (error) {
      console.error(`Error creating container ${containerName}:`, error);
      throw error;
    }
  }

  async deleteContainer(containerName: string): Promise<void> {
    try {
      await this.blobServiceClient.deleteContainer(containerName);
    } catch (error) {
      console.error(`Error deleting container ${containerName}:`, error);
      throw error;
    }
  }

  async getBlobUrl(containerName: string, blobName: string, folderPath: string = ''): Promise<string> {
    const containerClient: ContainerClient = this.blobServiceClient.getContainerClient(containerName);
    
    // Construct the full blob path
    const blobPath = folderPath 
      ? (folderPath.endsWith('/') ? folderPath + blobName : folderPath + '/' + blobName)
      : blobName;
    
    const blobClient = containerClient.getBlobClient(blobPath);
    return blobClient.url;
  }

  async downloadBlob(containerName: string, blobName: string, folderPath: string = ''): Promise<{ data: Blob; contentType: string }> {
    const containerClient: ContainerClient = this.blobServiceClient.getContainerClient(containerName);
    
    // Construct the full blob path
    const blobPath = folderPath 
      ? (folderPath.endsWith('/') ? folderPath + blobName : folderPath + '/' + blobName)
      : blobName;
    
    const blobClient = containerClient.getBlobClient(blobPath);
    
    try {
      // Use download() and prefer browser-provided blobBody when available
      const downloadResponse = await blobClient.download();

      let dataBlob: Blob | null = null;

      if (downloadResponse.blobBody) {
        // Browser environment: SDK exposes blobBody
        dataBlob = await downloadResponse.blobBody;
      } else if (downloadResponse.readableStreamBody) {
        // Fallback: coerce to a Web ReadableStream and convert via Response
        const webStream = downloadResponse.readableStreamBody as unknown as ReadableStream;
        const resp = new Response(webStream as any);
        dataBlob = await resp.blob();
      }

      if (!dataBlob) {
        throw new Error('Failed to download blob - response had no body');
      }

      // Determine the best content type
      let resolvedContentType = downloadResponse.contentType || '';
      if (!resolvedContentType) {
        try {
          const props = await blobClient.getProperties();
          resolvedContentType = props.contentType || '';
        } catch {}
      }
      if (!resolvedContentType) {
        // Fallback: basic inference from file extension
        const lower = blobName.toLowerCase();
        if (lower.endsWith('.pdf')) resolvedContentType = 'application/pdf';
        else if (lower.endsWith('.png')) resolvedContentType = 'image/png';
        else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) resolvedContentType = 'image/jpeg';
        else if (lower.endsWith('.gif')) resolvedContentType = 'image/gif';
        else if (lower.endsWith('.txt')) resolvedContentType = 'text/plain';
        else if (lower.endsWith('.json')) resolvedContentType = 'application/json';
        else if (lower.endsWith('.csv')) resolvedContentType = 'text/csv';
        else resolvedContentType = 'application/octet-stream';
      }

      // Ensure blob has the correct type; recreate if mismatched
      if (dataBlob.type !== resolvedContentType) {
        const ab = await dataBlob.arrayBuffer();
        dataBlob = new Blob([ab], { type: resolvedContentType });
      }

      return {
        data: dataBlob,
        contentType: resolvedContentType
      };
    } catch (error) {
      console.error(`Error downloading blob ${blobName}:`, error);
      throw error;
    }
  }

  // Generate a container-level SAS token that allows public uploads
  // Calls Azure Function backend to generate proper SAS tokens
  async generateContainerUploadSas(containerName: string, expiresInDays: number, userObjectId?: string, userEmail?: string): Promise<string> {
    try {
      // Azure Function endpoint (update this URL when deployed)
      const functionUrl = process.env.REACT_APP_FUNCTION_URL || 'http://localhost:7071/api';
      
      const requestBody = {
        containerName,
        expiresInDays,
        userObjectId: userObjectId || '',
        userEmail: userEmail || ''
      };

      const response = await fetch(`${functionUrl}/generate_sas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.sasToken;
    } catch (error) {
      console.error(`Error generating upload SAS for container ${containerName}:`, error);
      throw error;
    }
  }
} 